/**
 * Date formatting utilities with timezone and format configuration
 */

import { DEFAULT_LOCALE, getIntlLocale, normalizeLocale } from "@/utils/localeUtils.js";

const getTimezone = () => import.meta.env.VITE_TIMEZONE || "Europe/Tallinn";
const getLocale = () => {
  if (import.meta.env.VITE_LOCALE) {
    return import.meta.env.VITE_LOCALE;
  }
  if (typeof localStorage !== "undefined") {
    return getIntlLocale(normalizeLocale(localStorage.getItem("locale")));
  }
  return getIntlLocale(DEFAULT_LOCALE);
};
const getTimeFormat = () => import.meta.env.VITE_TIME_FORMAT || "24";

/**
 * Format a date/time string with configured timezone and format
 * @param {string|Date} dateInput - Date string or Date object
 * @param {Object} options - Intl.DateTimeFormat options (optional)
 * @returns {string} Formatted date string
 */
export const formatDateTime = (dateInput, options = {}) => {
  if (!dateInput) return "";

  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return "";

  const timezone = getTimezone();
  const locale = getLocale();
  const timeFormat = getTimeFormat();

  // Default options
  const defaultOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timezone,
    ...options
  };

  // Set hour format based on TIME_FORMAT env variable
  if (timeFormat === "12" && !options.hour12) {
    defaultOptions.hour12 = true;
  } else if (timeFormat === "24" && options.hour12 !== true) {
    defaultOptions.hour12 = false;
  }

  return date.toLocaleString(locale, defaultOptions);
};

/**
 * Format a date only (no time)
 * @param {string|Date} dateInput - Date string or Date object
 * @param {Object} options - Intl.DateTimeFormat options (optional)
 * @returns {string} Formatted date string
 */
export const formatDate = (dateInput, options = {}) => {
  if (!dateInput) return "";

  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return "";

  const timezone = getTimezone();
  const locale = getLocale();

  const defaultOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: timezone,
    ...options
  };

  return date.toLocaleDateString(locale, defaultOptions);
};

/**
 * Format a time only (no date)
 * @param {string|Date} dateInput - Date string or Date object
 * @param {Object} options - Intl.DateTimeFormat options (optional)
 * @returns {string} Formatted time string
 */
export const formatTime = (dateInput, options = {}) => {
  if (!dateInput) return "";

  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return "";

  const timezone = getTimezone();
  const locale = getLocale();
  const timeFormat = getTimeFormat();

  const defaultOptions = {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timezone,
    ...options
  };

  // Set hour format based on TIME_FORMAT env variable
  if (timeFormat === "12" && !options.hour12) {
    defaultOptions.hour12 = true;
  } else if (timeFormat === "24" && options.hour12 !== true) {
    defaultOptions.hour12 = false;
  }

  return date.toLocaleTimeString(locale, defaultOptions);
};
